import { intersection } from "lodash";

import { translate } from "app/filters/i18nFilter";
import { objectKeys } from "app/helpers/objects";
import type { SignalNameSegments } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameSegments";
import type { NewsCategoriesByType } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoriesByType";
import type { NewsCategoryType } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoryType";
import type NewsSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalFilters";
import NewsSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalSubType";
import type {
    I18nKeyModifier,
    TranslatableNameWithType,
    TranslationOptions,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import {
    getNewsSignalCategoryKey,
    matchNewsCategoryToName,
} from "app/pages/sales-intelligence/sub-modules/signals/utils/news/filters";
import type { Translatable } from "app/pages/sales-intelligence/types";

import type Signal from "../../../models/core/Signal";
import { appendTimeframeSuffix, buildTimeframeTranslatable } from "../shared/timeframeTranslatable";
import type UISignalNameBuilder from "../UISignalNameBuilder";
import { boldHtml, boldTranslatable } from "app/pages/sales-intelligence/sub-modules/signals/utils/boldSignalValue";
import { formatSelectionOverflowHtml } from "app/pages/sales-intelligence/sub-modules/signals/utils/signalSelectionOverflow";
import {
    getSelectionReplacements,
    isPartialFilterName,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";

export type FactoryDeps = {
    readonly newsCategories: Readonly<NewsCategoriesByType>;
};

export default function createNewsUISignalNameBuilder(deps: FactoryDeps): UISignalNameBuilder<NewsSignalFilters> {
    const getTranslationOptions = (signalSubType: NewsSignalSubType): Readonly<I18nKeyModifier> => {
        return {
            i18nKeyModifier: getNewsSignalCategoryKey(signalSubType),
        };
    };

    const getPartialSelectionName = ({
        option,
        remainingOptions,
    }: Readonly<TranslationOptions>): Readonly<Translatable> => {
        return {
            key: `${boldHtml(translate(option))}${formatSelectionOverflowHtml(remainingOptions)}`,
        };
    };

    const translatableFromFilterName = (filterName: Readonly<TranslatableNameWithType>): Readonly<Translatable> => {
        return filterName.translatable;
    };

    const translatableFromFilterNameForRichName = (
        filterName: Readonly<TranslatableNameWithType>,
    ): Readonly<Translatable> => {
        if (filterName.type === "partial") {
            const replacements = getSelectionReplacements(filterName);

            if (replacements) {
                return getPartialSelectionName(replacements);
            }
        }

        return filterName.translatable;
    };

    const isCategoryTypeSelected = ({
        categories,
    }: Partial<NewsSignalFilters>): ((category: NewsCategoryType) => boolean) => {
        return (newsCategory) => {
            const options = deps.newsCategories[newsCategory];
            const selectedCategories = intersection(options, categories);

            return selectedCategories.length === options.length;
        };
    };

    const buildNameForCategoryType = (
        signalSubType: NewsCategoryType,
        filters: Partial<NewsSignalFilters>,
    ): Readonly<TranslatableNameWithType> => {
        const options = deps.newsCategories[signalSubType];
        const translationOptions = getTranslationOptions(signalSubType);
        const nameBuilder = filterService.getFilterName(FilterType.NewsCategories);

        return nameBuilder.buildName(filters, options, translationOptions);
    };

    const buildNameForMixedCategories = (
        signalSubType: NewsSignalSubType,
        filters: Partial<NewsSignalFilters>,
    ): Readonly<TranslatableNameWithType> => {
        const translationOptions = getTranslationOptions(signalSubType);
        const nameBuilder = filterService.getFilterName(FilterType.AnyNewsCategories);

        return nameBuilder.buildName(filters, deps.newsCategories, translationOptions);
    };

    const buildNameForCategories = (
        signalSubType: NewsSignalSubType,
        filters: Partial<NewsSignalFilters>,
    ): Readonly<TranslatableNameWithType> => {
        const categoryTypeOptions = objectKeys(deps.newsCategories);
        const selectedCategoryTypes = categoryTypeOptions.filter(isCategoryTypeSelected(filters));

        if (selectedCategoryTypes.length === 1) {
            return buildNameForCategoryType(selectedCategoryTypes[0], filters);
        }

        return buildNameForMixedCategories(signalSubType, filters);
    };

    const buildCategoryFilterName = (
        signalType: string,
        signalSubType: NewsSignalSubType,
        filters: Partial<NewsSignalFilters>,
    ): Readonly<TranslatableNameWithType> => {
        switch (signalSubType) {
            case NewsSignalSubType.AnyNews:
                return buildNameForCategories(signalSubType, filters);
            case NewsSignalSubType.Leadership:
            case NewsSignalSubType.Growth:
            case NewsSignalSubType.Strategy:
            case NewsSignalSubType.Investment:
            case NewsSignalSubType.CompanyChallenges:
            case NewsSignalSubType.Recognition:
                return buildNameForCategoryType(signalSubType, filters);
            default:
                throw new Error(`Couldn't build name for ${signalType} signalSubType: ${signalSubType}`);
        }
    };

    const buildCategoryName = (
        signalType: string,
        signalSubType: NewsSignalSubType,
        filters: Partial<NewsSignalFilters>,
    ): Readonly<Translatable> => {
        return translatableFromFilterName(buildCategoryFilterName(signalType, signalSubType, filters));
    };

    const buildCategoryRichName = (
        signalType: string,
        signalSubType: NewsSignalSubType,
        filters: Partial<NewsSignalFilters>,
    ): Readonly<Translatable> => {
        return translatableFromFilterNameForRichName(buildCategoryFilterName(signalType, signalSubType, filters));
    };

    const resolveNewsCategoryLabels = (filters: Partial<NewsSignalFilters>): readonly string[] => {
        return (filters.categories ?? []).map((category) => translate(matchNewsCategoryToName(category).key));
    };

    const getNewsCategoryOptionsCount = (signalSubType: NewsSignalSubType): number => {
        if (signalSubType === NewsSignalSubType.AnyNews) {
            return objectKeys(deps.newsCategories).reduce(
                (total, categoryType) => total + deps.newsCategories[categoryType].length,
                0,
            );
        }

        return deps.newsCategories[signalSubType].length;
    };

    const isPartialNewsCategorySelection = (signal: Signal<NewsSignalFilters>): boolean => {
        const labels = resolveNewsCategoryLabels(signal.filters);
        const signalSubType = signal.signalSubType as NewsSignalSubType;

        if (labels.length < 2) {
            return false;
        }

        const filterName = buildCategoryFilterName(signal.signalType, signalSubType, signal.filters);

        if (filterName.type === "any") {
            return false;
        }

        if (isPartialFilterName(filterName)) {
            return true;
        }

        return labels.length < getNewsCategoryOptionsCount(signalSubType);
    };

    return {
        buildName({ signalType, signalSubType, filters }: Signal<NewsSignalFilters>): Readonly<Translatable> {
            const baseName = buildCategoryRichName(signalType, signalSubType as NewsSignalSubType, filters);

            return appendTimeframeSuffix({ key: boldTranslatable(baseName) }, filters);
        },
        buildNameSegments(signal: Signal<NewsSignalFilters>): SignalNameSegments {
            const { signalType, signalSubType, filters } = signal;
            const baseName = buildCategoryName(signalType, signalSubType as NewsSignalSubType, filters);

            return {
                signalType: baseName,
                signalData: buildTimeframeTranslatable(filters.period, filters.periodAmount),
                ...(isPartialNewsCategorySelection(signal) ? { selectionOverflowOn: "signalType" as const } : {}),
            };
        },
        buildSelectionTooltipLabels(signal: Signal<NewsSignalFilters>) {
            if (!isPartialNewsCategorySelection(signal)) {
                return undefined;
            }

            const labels = resolveNewsCategoryLabels(signal.filters);

            return labels.length > 1 ? { signalType: labels } : undefined;
        },
    };
}
